import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  useMediaQuery,
  useTheme,
  Paper,
  keyframes,
  Button
} from '@mui/material';
import { AddOutlined as AddIcon } from '@mui/icons-material';
import { Lightning, ArrowUp } from '@phosphor-icons/react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { Client } from '@langchain/langgraph-sdk';
import {
  updateCurrentThreadId,
  getCachedThreadId,
  cacheThreadId,
  ThreadType,
  loadThreadMap,
  saveThreadMap
} from '@/services/threadService';
import ReactMarkdown from 'react-markdown';

const LANGGRAPH_BASE_URL =
  import.meta.env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';

// Typing indicator animation (3 dots like ChatGPT)
const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
`;

const TypingIndicator: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
        py: 0.5
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: theme.palette.text.secondary,
            animation: `${bounce} 1.4s infinite ease-in-out`,
            animationDelay: `${i * 0.16}s`
          }}
        />
      ))}
    </Box>
  );
};

const getAuthToken = async (
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error) {
    return await getAccessTokenWithPopup(authParams);
  }
};

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  isStreaming?: boolean;
}

export interface SessionChatInterfaceProps {
  sessionId: string;
  initialThreadId?: string | null;
  compact?: boolean;
  onTranscriptUpdated?: () => Promise<void>;
}

const createLangGraphClient = (apiUrl: string, token: string, userId: string) =>
  new Client({
    apiUrl,
    defaultHeaders: {
      Authorization: `Bearer ${token}`,
      'X-User-Id': userId
    }
  });

export const SessionChatInterface: React.FC<SessionChatInterfaceProps> = ({
  sessionId,
  initialThreadId,
  compact = false,
  onTranscriptUpdated
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(
    initialThreadId || null
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasLoadedHistoryRef = useRef(false);

  // Auto-focus input on mount (but not on mobile to avoid keyboard popping up)
  useEffect(() => {
    if (!isMobile) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load thread ID and chat history on mount
  useEffect(() => {
    if (initialThreadId) {
      setThreadId(initialThreadId);
    } else {
      const cached = getCachedThreadId(sessionId, ThreadType.Chat);
      if (cached) {
        setThreadId(cached);
      }
    }

    // Load chat history
    const loadChatHistory = async () => {
      if (!sessionId || !appUser?.id || hasLoadedHistoryRef.current) return;

      hasLoadedHistoryRef.current = true;
      setIsLoadingHistory(true);

      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const API_BASE_URL =
          import.meta.env.VITE_API_BASE_URL || 'http://localhost:2024';
        const response = await fetch(
          `${API_BASE_URL}/transcriptionsessions/${sessionId}/chat?pageNumber=1&pageSize=100`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-User-Id': appUser.id
            }
          }
        );

        if (response.ok) {
          const result = await response.json();

          // Convert backend format to frontend format
          // Backend returns: { success: true, messages: [...] }
          // Role can be enum (0 = User, 1 = Assistant) or string ("User", "Assistant")
          if (result.messages && Array.isArray(result.messages)) {
            const loadedMessages: ChatMessage[] = result.messages.map(
              (msg: any) => {
                // Handle both enum (0, 1, 2) and string ("User", "Assistant") roles
                let role: 'user' | 'assistant' = 'assistant';
                if (
                  msg.role === 0 ||
                  msg.role === 'User' ||
                  msg.role?.toLowerCase() === 'user'
                ) {
                  role = 'user';
                } else if (
                  msg.role === 1 ||
                  msg.role === 'Assistant' ||
                  msg.role?.toLowerCase() === 'assistant'
                ) {
                  role = 'assistant';
                }

                return {
                  id: msg.id || uuidv4(),
                  role,
                  content: msg.content || '',
                  timestamp:
                    msg.timestamp || msg.createdAt || new Date().toISOString()
                };
              }
            );

            console.log('Loaded chat messages:', loadedMessages);
            setMessages(loadedMessages);
          }
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadChatHistory();
  }, [
    sessionId,
    initialThreadId,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  const resolveOrCreateThreadId = useCallback(
    async (client: Client): Promise<string> => {
      if (threadId) return threadId;

      const createdThread = await client.threads.create();
      const newThreadId = createdThread.thread_id;
      setThreadId(newThreadId);
      cacheThreadId(sessionId, newThreadId, ThreadType.Chat);

      // Save to backend
      if (appUser?.id && sessionId) {
        try {
          await updateCurrentThreadId(
            sessionId,
            appUser.id,
            newThreadId,
            getAccessTokenSilently,
            getAccessTokenWithPopup,
            ThreadType.Chat
          );
        } catch (error) {
          console.error('Failed to save thread ID to backend:', error);
        }
      }

      return newThreadId;
    },
    [
      threadId,
      sessionId,
      appUser?.id,
      getAccessTokenSilently,
      getAccessTokenWithPopup
    ]
  );

  const handleSend = useCallback(async () => {
    if (!input.trim() || isSending || !appUser?.id) return;

    const content = input.trim();
    setInput('');
    setIsSending(true);

    // Generate assistant message ID outside try block so it's accessible in catch
    const assistantMessageId = uuidv4();

    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      abortControllerRef.current = new AbortController();
      const client = createLangGraphClient(
        LANGGRAPH_BASE_URL,
        token,
        appUser.id
      );
      const resolvedThreadId = await resolveOrCreateThreadId(client);

      // Add user message
      const userMessage: ChatMessage = {
        id: uuidv4(),
        role: 'user',
        content,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, userMessage]);

      // Add streaming assistant message
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Stream from the single_session_chat_agent
      const stream = client.runs.stream(
        resolvedThreadId,
        'single_session_chat_agent',
        {
          input: {
            messages: [{ type: 'human', content }]
          },
          config: {
            configurable: {
              authorization: `Bearer ${token}`,
              session_id: sessionId,
              user_id: appUser.id
            }
          },
          metadata: {
            session_id: sessionId,
            thread_id: resolvedThreadId,
            transcription_session_id: sessionId,
            user_id: appUser.id
          },
          streamMode: ['messages', 'updates'],
          streamSubgraphs: true,
          signal: abortControllerRef.current.signal
        }
      );

      let accumulatedContent = '';

      for await (const chunk of stream as any) {
        console.log('Received chunk:', chunk);

        // Handle object format: {event: 'messages/partial', data: [...]}
        const event = chunk.event || chunk[0];
        const data = chunk.data || chunk[1];

        if (
          event === 'messages/partial' &&
          Array.isArray(data) &&
          data.length > 0
        ) {
          for (const msg of data) {
            if (msg?.type === 'ai' || msg?.role === 'assistant') {
              const newContent = msg.content || '';
              if (newContent && newContent !== accumulatedContent) {
                accumulatedContent = newContent;
                console.log(
                  'Updating with partial content:',
                  accumulatedContent
                );
                setMessages((prev) => {
                  const updated = [...prev];
                  const assistantMsgIdx = updated.findIndex(
                    (m) => m.id === assistantMessageId && m.role === 'assistant'
                  );
                  if (assistantMsgIdx >= 0) {
                    updated[assistantMsgIdx] = {
                      ...updated[assistantMsgIdx],
                      content: accumulatedContent,
                      isStreaming: true
                    };
                  }
                  return updated;
                });
              }
            }
          }
        } else if (
          event === 'messages/complete' &&
          Array.isArray(data) &&
          data.length > 0
        ) {
          for (const msg of data) {
            if (msg?.type === 'ai' || msg?.role === 'assistant') {
              const finalContent = msg.content || accumulatedContent;
              console.log('Finalizing with complete content:', finalContent);
              setMessages((prev) => {
                const updated = [...prev];
                const assistantMsgIdx = updated.findIndex(
                  (m) => m.id === assistantMessageId && m.role === 'assistant'
                );
                if (assistantMsgIdx >= 0) {
                  updated[assistantMsgIdx] = {
                    ...updated[assistantMsgIdx],
                    content: finalContent,
                    isStreaming: false
                  };
                }
                return updated;
              });
            }
          }
        } else if (
          (event === 'updates' || event?.startsWith('updates|')) &&
          data?.messages
        ) {
          // Handle 'updates' stream mode
          const messages = data.messages;
          for (const msg of messages) {
            if (msg?.type === 'ai' || msg?.role === 'assistant') {
              const newContent = msg.content || '';
              if (newContent && newContent !== accumulatedContent) {
                accumulatedContent = newContent;
                console.log(
                  'Updating with updates content:',
                  accumulatedContent
                );
                setMessages((prev) => {
                  const updated = [...prev];
                  const assistantMsgIdx = updated.findIndex(
                    (m) => m.id === assistantMessageId && m.role === 'assistant'
                  );
                  if (assistantMsgIdx >= 0) {
                    updated[assistantMsgIdx] = {
                      ...updated[assistantMsgIdx],
                      content: accumulatedContent,
                      isStreaming: true
                    };
                  }
                  return updated;
                });
              }
            }
          }
        }
      }

      // Finalize the streaming message
      setMessages((prev) => {
        const updated = [...prev];
        const assistantMsgIdx = updated.findIndex(
          (m) => m.id === assistantMessageId && m.role === 'assistant'
        );
        if (assistantMsgIdx >= 0) {
          updated[assistantMsgIdx] = {
            ...updated[assistantMsgIdx],
            isStreaming: false
          };
        }
        return updated;
      });

      // Check if transcript was updated (e.g., insights added)
      if (onTranscriptUpdated) {
        await onTranscriptUpdated();
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Chat request aborted');
      } else {
        console.error('Error sending chat message:', error);
        // Update the assistant message to show error
        setMessages((prev) => {
          const updated = [...prev];
          const assistantMsgIdx = updated.findIndex(
            (m) => m.id === assistantMessageId && m.role === 'assistant'
          );
          if (assistantMsgIdx >= 0) {
            updated[assistantMsgIdx] = {
              ...updated[assistantMsgIdx],
              content: 'Sorry, I encountered an error. Please try again.',
              isStreaming: false
            };
          }
          return updated;
        });
      }
    } finally {
      setIsSending(false);
      abortControllerRef.current = null;
    }
  }, [
    input,
    isSending,
    appUser?.id,
    sessionId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    resolveOrCreateThreadId,
    onTranscriptUpdated
  ]);

  const canSend = useMemo(
    () => !isSending && input.trim().length > 0 && !!appUser?.id,
    [isSending, input, appUser?.id]
  );

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = useCallback(async () => {
    // Clear current messages
    setMessages([]);
    // Clear current thread ID
    setThreadId(null);
    // Clear cached thread ID from localStorage
    if (sessionId) {
      const map = loadThreadMap();
      const key = `${sessionId}_${ThreadType.Chat}`;
      delete map[key];
      saveThreadMap(map);
    }
    // Reset history loading flag so we don't reload old messages
    hasLoadedHistoryRef.current = false;
    // Clear input
    setInput('');
    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsSending(false);
    // Focus the input after state updates
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [sessionId]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper'
      }}
    >
      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start', // Left-align all children
          width: '100%'
        }}
      >
        {/* New Chat Button */}
        {!compact && (
          <Box
            sx={{
              width: '100%',
              pb: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              mb: 1
            }}
          >
            <Button
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleNewChat}
              disabled={isSending}
              sx={{
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                borderColor: theme.palette.divider,
                color: 'text.primary',
                '&:hover': {
                  borderColor: theme.palette.divider,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : '#f9fafb'
                }
              }}
            >
              New Chat
            </Button>
          </Box>
        )}

        {isLoadingHistory && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              width: '100%'
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
              Loading chat history...
            </Typography>
          </Box>
        )}

        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              width: '100%',
              mb: message.role === 'assistant' ? 2.5 : 1.5
            }}
          >
            {message.role === 'user' ? (
              // User message: small dialog box, right-aligned
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '100%'
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: '75%',
                    p: 1.25,
                    px: 1.5,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.08)'
                        : '#f3f4f6',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    position: 'relative'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: theme.palette.text.primary,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {message.content}
                  </Typography>
                </Paper>
              </Box>
            ) : (
              // Assistant message: plain text below
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}
              >
                {message.isStreaming && !message.content ? (
                  <Box sx={{ pl: 0 }}>
                    <TypingIndicator />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      fontSize: '15px',
                      lineHeight: 1.7,
                      color: theme.palette.text.primary,
                      textAlign: 'left',
                      '& p': { m: 0, textAlign: 'left', mb: 1.5 },
                      '& p:last-child': { mb: 0 },
                      '& ul, & ol': { my: 1.5, pl: 2.5, textAlign: 'left' },
                      '& li': { my: 0.75, textAlign: 'left' },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        mt: 2,
                        mb: 1,
                        textAlign: 'left',
                        fontWeight: 600
                      },
                      '& h1': { fontSize: '1.5em' },
                      '& h2': { fontSize: '1.3em' },
                      '& h3': { fontSize: '1.1em' },
                      '& code': {
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.06)',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.375,
                        fontSize: '0.9em',
                        fontFamily: 'monospace'
                      },
                      '& pre': {
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.03)',
                        p: 1.5,
                        borderRadius: 0.5,
                        overflow: 'auto',
                        my: 1.5,
                        '& code': {
                          bgcolor: 'transparent',
                          p: 0
                        }
                      },
                      '& blockquote': {
                        borderLeft: `3px solid ${theme.palette.divider}`,
                        pl: 1.5,
                        my: 1.5,
                        fontStyle: 'italic',
                        color: theme.palette.text.secondary
                      },
                      '& a': {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }
                    }}
                  >
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ))}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : '#fafafa',
            transition: 'all 0.2s ease',
            '&:focus-within': {
              borderColor: '#6b7280',
              backgroundColor: theme.palette.background.paper
            }
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask about this file..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSending}
            variant="standard"
            inputRef={inputRef}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '13px',
                lineHeight: 1.5,
                '& textarea': {
                  '&::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 0.7
                  }
                }
              }
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!canSend}
            size="small"
            sx={{
              width: 28,
              height: 28,
              bgcolor: canSend
                ? '#1a1a1a'
                : theme.palette.action.disabledBackground,
              color: canSend ? 'white' : theme.palette.action.disabled,
              borderRadius: '6px',
              flexShrink: 0,
              '&:hover': {
                bgcolor: canSend
                  ? '#000'
                  : theme.palette.action.disabledBackground
              },
              '&:disabled': {
                bgcolor: theme.palette.action.disabledBackground
              }
            }}
          >
            {isSending ? (
              <CircularProgress size={14} sx={{ color: 'inherit' }} />
            ) : (
              <ArrowUp size={16} weight="bold" />
            )}
          </IconButton>
        </Box>

        {/* Keyboard hint */}
        <Typography
          sx={{
            fontSize: '11px',
            color: theme.palette.text.secondary,
            mt: 1,
            textAlign: 'left',
            opacity: 0.7
          }}
        >
          Press Enter to send, Shift+Enter for new line
        </Typography>
      </Box>
    </Box>
  );
};
